import {Separator} from 'platform/components';
import {
  Box,
  Center,
  Grid,
  GridItem,
  HStack,
  Show,
  Text,
  TextAlign,
  useDevice,
  VStack,
} from 'platform/foundation';
import {useFormatCurrency} from 'platform/locale';
import {match} from 'ts-pattern';

import {floor} from 'ramda-adjunct';

import {buildArray, isLastIndexInArray, suffixTestId, TestIdProps} from 'shared/web';

import i18n from '../../../../../i18n/i18n';
import {ColorSegment} from '../../../components/ColorSegment';
import {ColorSegmentColor} from '../../../types/ColorSegmentColor';

interface PriceRecommendationChartProps extends TestIdProps {
  currency: string;
  recommendations: number[];
}

export function PriceRecommendationChart(props: PriceRecommendationChartProps) {
  const formatCurrency = useFormatCurrency();
  const {recommendations, currency} = props;
  const device = useDevice();

  if (recommendations.length !== 5) {
    return null;
  }

  const segments = buildArray<{color: ColorSegmentColor; label: string; value: number}>([])
    .add({
      color: ColorSegmentColor.VeryLow,
      label: i18n.t('entity.priceRecommendationChart.conservative'),
      value: recommendations[0],
    })
    .when(device !== 'mobile', {
      color: ColorSegmentColor.Low,
      label: '',
      value: recommendations[1],
    })
    .add({
      color: ColorSegmentColor.Medium,
      label: i18n.t('entity.priceRecommendationChart.average'),
      value: recommendations[2],
    })
    .when(device !== 'mobile', {
      color: ColorSegmentColor.High,
      label: '',
      value: recommendations[3],
    })
    .add({
      color: ColorSegmentColor.VeryHigh,
      label: i18n.t('entity.priceRecommendationChart.aggressive'),
      value: recommendations[4],
    });

  const getLabelAlignment = (index: number) =>
    match<number, TextAlign>(index)
      .with(0, () => 'left')
      .with(segments.length - 1, () => 'right')
      .otherwise(() => 'center');

  return (
    <Box data-testid={suffixTestId('priceRecommendationChart', props)}>
      <Grid columns={device === 'mobile' ? 3 : 5} horizontalSpacing={0}>
        {segments.map((item, index) => (
          <GridItem key={item.color} data-testid={suffixTestId(`gridItem-${item.color}`, props)}>
            <HStack height="100%">
              <VStack grow={1} spacing={1} align="center" justify="flex-end">
                <Box width="100%" paddingBottom={6}>
                  <Text
                    align={getLabelAlignment(index)}
                    size="xSmall"
                    color="secondary"
                    data-testid={suffixTestId(`label-${item.color}`, props)}
                  >
                    {item.label}
                  </Text>
                </Box>

                <ColorSegment
                  $color={item.color}
                  $isFirst={index === 0}
                  $isLast={isLastIndexInArray(segments, index)}
                  data-testid={suffixTestId(`colorSegment-${item.color}`, props)}
                />

                <Box height={12}>
                  <Center height="100%">
                    <Text
                      color="link"
                      size={index === floor(segments.length / 2) ? 'large' : 'base'}
                      alternative={index === floor(segments.length / 2)}
                      data-testid={suffixTestId(`text-${item.color}`, props)}
                    >
                      {formatCurrency(item.value, currency)}
                    </Text>
                  </Center>
                </Box>
              </VStack>

              <Show when={index < segments.length - 1}>
                <Separator orientation="vertical" spacing={0} />
              </Show>
            </HStack>
          </GridItem>
        ))}
      </Grid>
    </Box>
  );
}
